import { ExpandLess, ExpandMore, InsertDriveFile, CloudDownload, Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Paper,
  TextField,
  Typography,
  Backdrop,
  Link,
  CircularProgress,
} from "@mui/material";

import { styled } from "@mui/system";
import LoadingScreen from "components/Loading";
import MDButton from "components/MDButton";
import { useAuth } from "context/authContext";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { acceptTargetORAch } from "services/api";
import { deactivateChat } from "services/api";
import { getChatroom } from "services/api";
import { getFABChatData } from "services/api";
import { rejectTargetORAch } from "services/api";
import { getChatKpiData } from "services/api";
import { getTenantAndToken } from "services/api";
import { createNewStompClient } from "services/api";
import { getOldMessagesByChatRoom } from "services/api";
import { uploadChatFile } from "services/api";
import { downloadChatMessageFile } from "services/api";
import { getChatExport } from "services/api";
import { getPhoto } from "services/api";
import EmployeeAvatar from "components/EmployeeAvatar";
import { useTranslation } from "react-i18next";

const MAX_FILE_SIZE = 20 * 1024 * 1024;

// Styled drop area
const DropOverlay = styled(Backdrop)(({ theme }) => ({
  zIndex: 10,
  color: "#fff",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const ChatPage = () => {
  const { setChatsChanged, chatschanged } = useAuth();
  const { t, i18n } = useTranslation();
  const { chatRoomId } = useParams();
  const location = useLocation();
  const { employee } = useAuth();
  const chat = location.state?.chat;
  const [kpiData, setKpiData] = useState(new Array(20));
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [stompClient, setStompClient] = useState(createNewStompClient(chatRoomId));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [chatRoom, setChatRoom] = useState();
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [names, setNames] = useState();
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const fetchData = async () => {
      if (!loading) setLoading(true);
      const messageChat = await getData();
      // Step 1: Extract unique senderIds from messageChat, excluding null values
      const uniqueSenderIds = [
        ...new Set(
          messageChat
            ?.map((message) => message.senderId) // Extract senderIds
            .filter((senderId) => senderId !== null) // Filter out null values
        ),
      ];
      // Step 2: Call getPhoto for each unique senderId
      const senderDetails = await Promise.all(
        uniqueSenderIds.map(async (senderId) => {
          try {
            const photoData = await getPhoto(senderId);
            // Ensure photoData is an array with at least two elements
            if (Array.isArray(photoData)) {
              const [photo, name] = photoData;
              return { senderId, photo, name };
            } else {
              return { senderId, photo: null, name: "Unknown" };
            }
          } catch (error) {
            return { senderId, photo: null, name: "Unknown" };
          }
        })
      );
      // // Step 3: Create mappings for senderId to photo and senderId to name
      const senderPhotoMap = senderDetails.reduce((map, { senderId, photo }) => {
        map[senderId] = photo || null; // Handle empty photo
        return map;
      }, {});

      const senderNameMap = senderDetails.reduce((map, { senderId, name }) => {
        map[senderId] = name || "Unknown"; // Handle empty name
        return map;
      }, {});

      setPhotos(senderPhotoMap);
      setNames(senderNameMap);
      const [token, tenant] = getTenantAndToken();
      // Handle connection and subscribe
      stompClient.onConnect = () => {
        // Subscribe to the chat room messages AFTER connection
        const subscription = stompClient.subscribe(
          `/topic/chat/${chatRoomId}`,
          (message) => {
            const receivedMessage = JSON.parse(message.body);
            if (receivedMessage.senderId === employee.id) return;
            setMessages((prev) => [...prev, receivedMessage]);
          },
          {
            Authorization: token,
            "X-Tenant-ID": tenant,
          }
        );

        // Store the subscription for cleanup
        stompClient.subscription = subscription;
      };

      // Activate WebSocket connection
      stompClient.activate();
    };

    fetchData().then((e) => {
      setLoading(false);
    });

    return () => {
      if (stompClient && stompClient.subscription) {
        stompClient.subscription.unsubscribe(); // Unsubscribe when component unmounts
      }
      if (stompClient) {
        stompClient.deactivate(); // Disconnect WebSocket on cleanup
      }
    };
  }, [chatRoomId]);

  const getData = async () => {
    try {
      const [data, mess, chats] = await Promise.all([
        getChatKpiData(chat.performanceId),
        getOldMessagesByChatRoom(chatRoomId, employee.id),
        getChatroom(chatRoomId),
      ]);

      if (data && data !== "error") {
        setKpiData(data[0]);
      }

      if (mess !== "error") {
        setMessages(mess);
      }

      if (chats !== "error") {
        setChatRoom(chats);
      }

      setChatsChanged(!chatschanged);
      return mess;
    } catch (error) {
      console.error("Error fetching chat data:", error);
      return "error";
    }
  };

  function isNumeric(str) {
    return /^[+-]?\d+(\.\d+)?$/.test(str);
  }

  const sendMessage = () => {
    if (stompClient && stompClient.connected && isNumeric(chatRoomId) && selectedFile) {
      //chatRoomId, senderId, content, file
      setUploadLoading(true);
      uploadChatFile(chatRoomId, employee.id, messageText, selectedFile).then((e) => {
        if (e != "error") {
          setMessages((prev) => [...prev, e]);
        }
        setUploadLoading(false);
      });
      setSelectedFile(null);
      setMessageText("");
      return;
    }
    if (stompClient && stompClient.connected && messageText.trim() && isNumeric(chatRoomId)) {
      const chatMessage = {
        chatRoomId: parseInt(chatRoomId, 10),
        senderId: employee.id,
        content: messageText,
      };
      const [auth, tenant] = getTenantAndToken();
      // Include headers
      const headers = {
        Authorization: auth, // Add JWT token
        "X-Tenant-ID": tenant, // Add tenant ID
      };

      stompClient.publish({
        destination: "/app/chat.sendMessage",
        body: JSON.stringify(chatMessage),
        headers: headers,
      });

      setMessages((prev) => [...prev, chatMessage]);
      setMessageText("");
    }
  };

  const handleApproveTarget = async (id, accept) => {
    setLoading(true);
    //status accept=true reject=false
    if (accept) {
      if (kpiData[9] == 2) {
        //Update the performance to show that he accepted the target
        await acceptTargetORAch(id, true);
      } else {
        //update the performance and make chat active=false
        await acceptTargetORAch(id, true);
        await deactivateChat(chatRoomId);
        setChatsChanged(!chatschanged);
      }
    } else {
      //then achivement is already rejected just change the value of approved target to 2 and send a message
      await rejectTargetORAch(id, true);
    }
    await getData();
    setOpen(!open);
    setLoading(false);
  };

  const handleApproveAchievement = async (id, accept) => {
    setLoading(true);
    //status accept=true reject=false
    if (accept) {
      if (kpiData[8] == 2) {
        //Update the performance to show that he accepted the target
        await acceptTargetORAch(id, false);
      } else {
        //update the performance and make chat active=false
        await acceptTargetORAch(id, false);
        await deactivateChat(chatRoomId);
        setChatsChanged(!chatschanged);
      }
    } else {
      //then achivement is already rejected just change the value of approved target to 2 and send a message
      await rejectTargetORAch(id, false);
    }
    await getData();
    setOpen(!open);
    setLoading(false);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      // 10MB limit
      alert("File is too large. Max 20MB allowed.");
      return;
    }
    setSelectedFile(file);
  };

  // Handle drag events
  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    if (event.dataTransfer.files.length > 0) {
      setSelectedFile(event.dataTransfer.files[0]);
    }
  };

  const handleDownload = async () => {
    setDownloadLoading(true);
    await getChatExport(chatRoomId, chat?.kpiName);
    setDownloadLoading(false);
  };

  const toArabicNumbers = (num) => num.toString().replace(/\d/g, (d) => "٠١٢٣٤٥٦٧٨٩"[d]);

  return (
    <>
      {/* Full-Screen Chat Container */}
      {loading ? (
        <LoadingScreen />
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 64px)", // Adjust for Navbar height
            width: "100%",
            maxWidth: "100%",
            p: 2,
          }}
        >
          {/* Wrapper to align button to the right */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <MDButton
              variant="contained"
              color="primary"
              size="small" // Makes the button smaller
              startIcon={downloadLoading ? null : <CloudDownload />}
              onClick={handleDownload}
              disabled={downloadLoading}
              sx={{
                fontWeight: "bold",
                textTransform: "none",
                borderRadius: "8px",
                boxShadow: 3,
                minWidth: "auto", // Prevents extra padding
                padding: "4px 12px", // Custom padding to make it more compact
              }}
            >
              {downloadLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("common.export")
              )}
            </MDButton>
          </Box>
          {/* Chat Header */}
          <Paper
            elevation={3}
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: "primary.main",
              color: "white",
              borderRadius: 2,
              cursor: "pointer",
              "&:hover": { opacity: 0.85 }, // Dim on hover
            }}
            onClick={() => setOpen(!open)}
          >
            {/* KPI Name (Ellipsis for long text) */}
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "60%", // Prevents long names from pushing other content
                color: "#FFF", // Gold color for better contrast
              }}
            >
              {chat?.kpiName || "Chat Room"}
            </Typography>

            {/* KPI Date & Expand Button */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {kpiData && (
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: "500",
                    color: "#FFF", // Lighter text for visibility
                  }}
                >
                  {/* {`${kpiData[0]}/${kpiData[1]}`} */}
                  {i18n.language === "ar"
                    ? `${toArabicNumbers(kpiData?.[1] ?? "")}/${toArabicNumbers(
                        kpiData?.[0] ?? ""
                      )}`
                    : `${kpiData?.[0] ?? ""}/${kpiData?.[1] ?? ""}`}
                </Typography>
              )}
              <IconButton size="small" sx={{ color: "white" }}>
                {open ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>
          </Paper>

          {/* KPI Details (Dropdown) */}
          <Collapse in={open} sx={{ width: "100%", zIndex: 10 }}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                mt: 1,
                borderRadius: 2,
                backgroundColor: "background.paper",
              }}
            >
              {kpiData && (
                <Box sx={{ display: "grid", gap: 1 }}>
                  <Typography>
                    <strong>{t("user.myScorecard.perspective")}:</strong>{" "}
                    {t(`user.assess.perspectives.${kpiData?.[2]}`)}
                  </Typography>
                  <Typography>
                    <strong>{t("user.myScorecard.weight")}:</strong> {kpiData[3]}
                  </Typography>
                  <Box>
                    <Typography>
                      <strong>{t("user.myScorecard.target")}:</strong> {kpiData[4]}
                    </Typography>
                    {kpiData[10] == employee.id && (kpiData[8] === 0 || kpiData[8] === 2) && (
                      <>
                        <Button
                          sx={{
                            ml: 1,
                            color: "green",
                            "&:hover": { backgroundColor: "green", color: "#fff" },
                            borderRadius: 16,
                          }}
                          size="small"
                          onClick={() => handleApproveTarget(chatRoom?.performanceId, true)}
                        >
                          {t("common.accept")}
                        </Button>
                      </>
                    )}
                    {kpiData[10] == employee.id && kpiData[8] === 0 && (
                      <Button
                        sx={{
                          ml: 1,
                          color: "red",
                          "&:hover": { backgroundColor: "red", color: "#fff" },
                          borderRadius: 16,
                        }}
                        size="small"
                        onClick={() => handleApproveTarget(chatRoom?.performanceId, false)}
                      >
                        {t("common.reject")}
                      </Button>
                    )}
                  </Box>
                  <Box>
                    <Typography>
                      <strong>{t("user.myScorecard.achievement")}:</strong> {kpiData[5]}
                    </Typography>
                    {kpiData[10] == employee.id && (kpiData[9] === 0 || kpiData[9] === 2) && (
                      <>
                        <Button
                          sx={{
                            ml: 1,
                            color: "green",
                            "&:hover": { backgroundColor: "green", color: "#fff" },
                            borderRadius: 16,
                          }}
                          size="small"
                          onClick={() => handleApproveAchievement(chatRoom?.performanceId, true)}
                        >
                          {t("common.accept")}
                        </Button>
                      </>
                    )}
                    {kpiData[10] == employee.id && kpiData[9] === 0 && (
                      <Button
                        sx={{
                          ml: 1,
                          color: "red",
                          "&:hover": { backgroundColor: "red", color: "#fff" },
                          borderRadius: 16,
                        }}
                        size="small"
                        onClick={() => handleApproveAchievement(chatRoom?.performanceId, false)}
                      >
                        {t("common.reject")}
                      </Button>
                    )}
                  </Box>

                  <Typography>
                    <strong>{t("user.myScorecard.secondaryAchievement")}:</strong>{" "}
                    {kpiData[6] ?? t("user.competencies.none")}
                  </Typography>
                  <Typography>
                    <strong>{t("user.myScorecard.formula")}:</strong>
                    {/* {kpiData[7]} */}
                    {kpiData?.[7] === "Regular"
                      ? t("user.myScorecard.regular")
                      : t("user.myScorecard.reverse")}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Collapse>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100vh", // Ensure it takes full screen height
              overflow: "hidden", // Needed for overlay positioning
            }}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {/* Drag & Drop Overlay */}
            {isDragging && (
              <DropOverlay open={isDragging} onDragLeave={handleDragLeave}>
                <Typography variant="h6">{t("user.myScorecard.dropFileHere")}</Typography>
              </DropOverlay>
            )}

            {/* Chat Messages (Scrollable) */}
            <Box
              sx={{
                flexGrow: 1, // Take all available vertical space
                overflowY: "auto", // Make scrollable
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {messages.map((msg, index) =>
                !msg.senderId ? (
                  // System message styling
                  <Box
                    key={index}
                    sx={{
                      alignSelf: "center",
                      textAlign: "center",
                      fontWeight: "bold",
                      my: { xs: 0.5, sm: 1 }, // Smaller margin on small screens
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "0.875rem", sm: "1rem" }, // Smaller font on small screens
                        fontWeight: "bold",
                      }}
                    >
                      <b>{msg.content}</b>
                    </Typography>
                  </Box>
                ) : (
                  <Box
                    key={index}
                    sx={{
                      alignSelf: msg.senderId === employee.id ? "flex-end" : "flex-start",
                      maxWidth: { xs: "90%", sm: "70%" }, // Smaller maxWidth on small screens
                      bgcolor: msg.senderId === employee.id ? "primary.light" : "grey.300",
                      color: msg.senderId === employee.id ? "white" : "black",
                      padding: { xs: "8px 12px", sm: "12px 16px" }, // Smaller padding on small screens
                      borderRadius: 5,
                      boxShadow: 2,
                      display: "flex",
                      flexDirection: "column",
                      gap: { xs: 0.5, sm: 1 }, // Smaller gap on small screens
                    }}
                  >
                    {/* Sender Name and Avatar */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: 0.5, sm: 1 }, // Smaller gap on small screens
                      }}
                    >
                      {/* Avatar */}
                      <EmployeeAvatar
                        photo={photos[msg.senderId]}
                        sx={{ width: { xs: 20, sm: 24 }, height: { xs: 20, sm: 24 } }} // Smaller avatar on small screens
                      />

                      {/* Sender Name */}
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: "bold", fontSize: { xs: "0.75rem", sm: "0.875rem" } }} // Smaller font on small screens
                      >
                        {msg.senderId === employee.id
                          ? t("user.myScorecard.you")
                          : `${names[msg.senderId]}`}
                      </Typography>
                    </Box>

                    {/* Message Content (if exists) */}
                    {msg.content && (
                      <Typography
                        sx={{
                          wordBreak: "break-word",
                          fontSize: { xs: "0.875rem", sm: "1rem" }, // Smaller font on small screens
                        }}
                      >
                        {msg.content}
                      </Typography>
                    )}

                    {/* File Attachment (if exists) */}
                    {msg.fileName && msg.fileType && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: { xs: 0.5, sm: 1 }, // Smaller gap on small screens
                          p: { xs: 0.5, sm: 1 }, // Smaller padding on small screens
                          mt: { xs: 0.5, sm: 1 }, // Smaller margin on small screens
                          borderRadius: 2,
                          border: "1px solid",
                          borderColor: msg.senderId === employee.id ? "white" : "grey.500",
                          bgcolor:
                            msg.senderId === employee.id
                              ? "rgba(255, 255, 255, 0.2)"
                              : "rgba(0, 0, 0, 0.05)",
                          cursor: "pointer",
                          transition: "background-color 0.2s ease, transform 0.1s ease",
                          maxWidth: "100%",
                          "&:hover": {
                            backgroundColor:
                              msg.senderId === employee.id
                                ? "rgba(255, 255, 255, 0.3)"
                                : "rgba(0, 0, 0, 0.1)",
                            transform: "scale(1.02)",
                          },
                        }}
                        onClick={() => downloadChatMessageFile(msg.id, msg.fileName)}
                      >
                        <InsertDriveFile
                          sx={{
                            color: msg.senderId === employee.id ? "white" : "black",
                            flexShrink: 0,
                            fontSize: { xs: "1rem", sm: "1.25rem" }, // Smaller icon on small screens
                          }}
                        />
                        <Typography
                          sx={{
                            color: msg.senderId === employee.id ? "white" : "black",
                            fontWeight: "bold",
                            textDecoration: "none",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxWidth: { xs: "60vw", sm: "70vw" }, // Smaller maxWidth on small screens
                            fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Smaller font on small screens
                            "&:hover": { textDecoration: "underline" },
                          }}
                        >
                          {msg.fileName}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )
              )}
              <div ref={bottomRef} />
            </Box>

            {/* Message Input (Sticky at Bottom) */}
            {!chatRoom?.active ? (
              <Box display="flex" justifyContent="center" alignItems="center" sx={{ p: 2 }}>
                <Typography sx={{ color: "gray" }}>{t("user.myScorecard.chatEnded")}</Typography>
              </Box>
            ) : uploadLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box
                sx={{ display: "flex", flexDirection: "column", p: 2, borderTop: "1px solid #ccc" }}
              >
                {/* File Preview */}
                {selectedFile && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="body2" color="primary">
                      {t("user.myScorecard.selectedFile")}: {selectedFile.name}
                    </Typography>
                    <IconButton size="small" onClick={() => setSelectedFile(null)}>
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                {/* Chat Input & Attach Button */}
                <Box
                  sx={{ display: "flex", justifyContent: "center" }} // center in parent
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      width: "90%", // take only 70% width
                    }}
                  >
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder={t("user.myScorecard.typeMessage")}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                    />
                    <input
                      type="file"
                      style={{ display: "none" }}
                      id="fileInput"
                      onChange={handleFileSelect}
                    />
                    <label htmlFor="fileInput">
                      <IconButton component="span">
                        <AttachFileIcon />
                      </IconButton>
                    </label>
                    <IconButton color="primary" onClick={sendMessage}>
                      <SendIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};

export default ChatPage;
